// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import type {GuildBan} from '@app/features/guild/commands/GuildCommands';
import styles from '@app/features/moderation/components/modals/BanDetailsModal.module.css';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {Avatar} from '@app/features/ui/components/Avatar';
import Users from '@app/features/user/state/Users';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import * as DateUtils from '@app/features/user/utils/dateFormatting';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useState} from 'react';

interface BanDetailsModalProps {
	ban: GuildBan;
	onRevoke?: () => void;
}
export const BanDetailsModal: React.FC<BanDetailsModalProps> = observer(({ban, onRevoke}) => {
	const {t} = useLingui();
	const moderator = Users.getUser(ban.moderator_id);
	const avatarUrl = AvatarUtils.getUserAvatarURL(ban.user, false);
	const [isRevoking, setIsRevoking] = useState(false);
	const userTag = ban.user.tag ?? `${ban.user.username}#${(ban.user.discriminator ?? '').padStart(4, '0')}`;
	const handleRevoke = useCallback(async () => {
		if (!onRevoke) return;
		setIsRevoking(true);
		try {
			await onRevoke();
		} finally {
			setIsRevoking(false);
		}
		ModalCommands.popByType(BanDetailsModal);
	}, [onRevoke]);
	return (
		<Modal.Root size="small" centered>
			<Modal.Header title={t`Ban Details`} />
			<Modal.Content>
				<Modal.ContentLayout>
					<div className={styles.userSection}>
						{avatarUrl ? (
							<img src={avatarUrl} alt="" className={styles.avatar} />
						) : (
							<div className={styles.avatarPlaceholder}>{ban.user.username[0].toUpperCase()}</div>
						)}
						<div className={styles.userInfo}>
							<span className={styles.username}>{ban.user.username}</span>
							<span className={styles.tag}>{userTag}</span>
						</div>
					</div>
					<div className={styles.details}>
						<div className={styles.detailRow}>
							<span className={styles.detailLabel}>
								<Trans>Reason</Trans>
							</span>
							<span className={styles.detailValue}>
								{ban.reason || (
									<span className={styles.noReason}>
										<Trans>No reason provided</Trans>
									</span>
								)}
							</span>
						</div>
						<div className={styles.detailRow}>
							<span className={styles.detailLabel}>
								<Trans>Banned on</Trans>
							</span>
							<span className={styles.detailValue}>{DateUtils.getFormattedShortDate(new Date(ban.banned_at))}</span>
						</div>
						{ban.expires_at && (
							<div className={styles.detailRow}>
								<span className={styles.detailLabel}>
									<Trans>Expires</Trans>
								</span>
								<span className={styles.detailValue}>{DateUtils.getFormattedShortDate(new Date(ban.expires_at))}</span>
							</div>
						)}
						<div className={styles.detailRow}>
							<span className={styles.detailLabel}>
								<Trans>Banned by</Trans>
							</span>
							<span className={styles.detailValue}>
								{moderator ? (
									<span className={styles.moderator}>
										<Avatar user={moderator} size={20} />
										<span>{moderator.username}</span>
									</span>
								) : (
									<span className={styles.unknownModerator}>
										<Trans>Unknown</Trans>
									</span>
								)}
							</span>
						</div>
					</div>
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer>
				<Button variant="secondary" onClick={() => ModalCommands.pop()}>
					<Trans>Close</Trans>
				</Button>
				{onRevoke && (
					<Button variant="danger-primary" submitting={isRevoking} onClick={handleRevoke}>
						<Trans>Revoke Ban</Trans>
					</Button>
				)}
			</Modal.Footer>
		</Modal.Root>
	);
});
